import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLazyQuery } from '@apollo/client';
import Box from '@mui/material/Box';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import LinkOffRoundedIcon from '@mui/icons-material/LinkOffRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';

import { DrawerV2, ButtonV2, dsV2 } from '@bloomays-lib/ui.shared';
import { GET_SOCIETY_MISSIONS } from '@bloomays-lib/adapter.api-bloomer';
import { internationalPhoneRegex, mailRegex, nameRegex } from '@bloomays-lib/utils.shared';
import type { IContact } from '@bloomays-lib/types.shared';

import { FieldV2, InputV2, Row2, InfoV2, DividerV2, SectionHeading } from '../formFields';
import { SrcBadge } from '../components';
import type { ContactSlot } from '../../../../helpers/missionDashboardV2';
import { useContactMutations } from '../../../../hooks/useContactMutations';

export type ContactSource = 'mission' | 'society';

export type ContactEditDrawerProps = {
  open: boolean;
  onClose: () => void;
  contact: IContact;
  slot: ContactSlot;
  /** Niveau d'où provient le contact résolu (mission prioritaire / société repli). */
  source: ContactSource;
  missionId: string;
  societyId?: string;
  societyName?: string;
  onDone?: () => void | Promise<unknown>;
  /** Ouvre la fiche société correspondante (repli). */
  onOpenSociety?: () => void;
};

type SocietyMissionsData = {
  society?: { recordId: string; name?: string; mission?: { recordId: string; label?: string; isActive?: boolean }[] }[];
};

/**
 * Édition d'un contact (§8) via `updateContact` + déliaison de l'emplacement. Au niveau société
 * (= repli global), la déliaison demande une confirmation listant les missions impactées
 * (décision spec #5, via `GET_SOCIETY_MISSIONS`). Le niveau (mission / société) est affiché en
 * lecture seule via le badge de source.
 */
export const ContactEditDrawer = ({
  open,
  onClose,
  contact,
  slot,
  source,
  missionId,
  societyId,
  societyName,
  onDone,
  onOpenSociety,
}: ContactEditDrawerProps) => {
  const { t } = useTranslation(['missionDashboard', 'notify']);
  const NAME_ERROR = t('drawers.common.nameErrorFormat', { ns: 'missionDashboard' });
  const MAIL_ERROR = t('drawers.common.mailErrorFormat', { ns: 'missionDashboard' });
  const PHONE_ERROR = t('drawers.common.phoneErrorFormat', { ns: 'missionDashboard' });

  const mutations = useContactMutations({ missionId, onDone });

  const [firstname, setFirstname] = useState(contact.firstname ?? '');
  const [lastname, setLastname] = useState(contact.lastname ?? '');
  const [email, setEmail] = useState(contact.email ?? '');
  const [phoneNumber, setPhoneNumber] = useState(contact.phoneNumber ?? '');
  const [nameJob, setNameJob] = useState(contact.nameJob ?? '');
  const [confirming, setConfirming] = useState(false);

  const [fetchSocietyMissions, { data: societyData, loading: loadingMissions }] =
    useLazyQuery<SocietyMissionsData>(GET_SOCIETY_MISSIONS);

  const trimmed = {
    firstname: firstname.trim(),
    lastname: lastname.trim(),
    email: email.trim(),
    phoneNumber: phoneNumber.trim(),
    nameJob: nameJob.trim(),
  };
  const errors = {
    firstname: trimmed.firstname.length < 1 || !nameRegex.test(trimmed.firstname) ? NAME_ERROR : undefined,
    lastname: trimmed.lastname.length < 1 || !nameRegex.test(trimmed.lastname) ? NAME_ERROR : undefined,
    email: trimmed.email.length < 1 || !mailRegex.test(trimmed.email) ? MAIL_ERROR : undefined,
    phoneNumber: trimmed.phoneNumber && !internationalPhoneRegex.test(trimmed.phoneNumber) ? PHONE_ERROR : undefined,
  };
  const hasError = Object.values(errors).some(Boolean);
  const contactId = contact.recordId ?? '';

  const save = async () => {
    if (hasError || !contactId) return;
    const ok = await mutations.updateContact({
      recordId: contactId,
      firstname: trimmed.firstname,
      lastname: trimmed.lastname,
      email: trimmed.email,
      phoneNumber: trimmed.phoneNumber || undefined,
      nameJob: trimmed.nameJob || undefined,
    });
    if (ok) onClose();
  };

  const startUnlink = () => {
    if (source === 'society' && societyId) void fetchSocietyMissions({ variables: { id: societyId } });
    setConfirming(true);
  };

  const confirmUnlink = async () => {
    if (!contactId) return;
    const ok =
      source === 'society' && societyId && slot.societyRole
        ? await mutations.unlinkFromSociety(contactId, societyId, slot.societyRole)
        : await mutations.unlinkFromMission(contactId, slot.missionRole, slot.side);
    if (ok) onClose();
  };

  const impactedMissions = societyData?.society?.[0]?.mission ?? [];

  return (
    <DrawerV2
      open={open}
      onClose={onClose}
      title={t('drawers.editContact.title', { ns: 'missionDashboard' })}
      subtitle={t('drawers.addContact.subtitle', {
        ns: 'missionDashboard',
        side:
          slot.side === 'supplier'
            ? t('drawers.manageContacts.supplier', { ns: 'missionDashboard' })
            : t('drawers.manageContacts.client', { ns: 'missionDashboard' }),
        role: slot.label,
      })}
      width={480}
      footer={
        <>
          <ButtonV2 variant="secondary" onClick={onClose}>
            {t('drawers.common.cancel', { ns: 'missionDashboard' })}
          </ButtonV2>
          <ButtonV2
            onClick={save}
            disabled={hasError || mutations.busy}
            loading={mutations.busy}
            startIcon={<SaveRoundedIcon sx={{ fontSize: 15 }} />}
          >
            {t('drawers.common.save', { ns: 'missionDashboard' })}
          </ButtonV2>
        </>
      }
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
        <Box
          sx={{
            fontSize: 11,
            fontWeight: 700,
            color: dsV2.color.t3,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}
        >
          {t('drawers.editContact.source', { ns: 'missionDashboard' })}
        </Box>
        <SrcBadge source={source} />
      </Box>

      <Row2>
        <FieldV2 label={t('drawers.common.firstname', { ns: 'missionDashboard' })} required error={errors.firstname}>
          <InputV2
            value={firstname}
            onChange={setFirstname}
            placeholder={t('drawers.addContact.firstnamePlaceholder', { ns: 'missionDashboard' })}
          />
        </FieldV2>
        <FieldV2 label={t('drawers.common.lastname', { ns: 'missionDashboard' })} required error={errors.lastname}>
          <InputV2
            value={lastname}
            onChange={setLastname}
            placeholder={t('drawers.addContact.lastnamePlaceholder', { ns: 'missionDashboard' })}
          />
        </FieldV2>
      </Row2>
      <FieldV2 label={t('drawers.common.email', { ns: 'missionDashboard' })} required error={errors.email}>
        <InputV2
          type="email"
          value={email}
          onChange={setEmail}
          placeholder={t('drawers.common.emailPlaceholder', { ns: 'missionDashboard' })}
        />
      </FieldV2>
      <FieldV2 label={t('drawers.common.phone', { ns: 'missionDashboard' })} error={errors.phoneNumber}>
        <InputV2
          type="tel"
          value={phoneNumber}
          onChange={setPhoneNumber}
          placeholder={t('drawers.common.optional', { ns: 'missionDashboard' })}
        />
      </FieldV2>
      <FieldV2 label={t('drawers.common.jobTitle', { ns: 'missionDashboard' })}>
        <InputV2
          value={nameJob}
          onChange={setNameJob}
          placeholder={t('drawers.common.optional', { ns: 'missionDashboard' })}
        />
      </FieldV2>

      <DividerV2 />
      <SectionHeading>{t('drawers.editContact.linkSection', { ns: 'missionDashboard' })}</SectionHeading>

      {!confirming ? (
        <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <ButtonV2
            variant="secondary"
            onClick={startUnlink}
            disabled={mutations.busy}
            startIcon={<LinkOffRoundedIcon sx={{ fontSize: 15 }} />}
          >
            {t('drawers.editContact.unlinkContact', { ns: 'missionDashboard' })}
          </ButtonV2>
          {source === 'society' && onOpenSociety ? (
            <ButtonV2
              variant="secondary"
              onClick={onOpenSociety}
              startIcon={<OpenInNewRoundedIcon sx={{ fontSize: 15 }} />}
            >
              {t('drawers.editContact.openSociety', { ns: 'missionDashboard' })}
            </ButtonV2>
          ) : null}
        </Box>
      ) : (
        <Box>
          {source === 'society' ? (
            <>
              <InfoV2 tone="amber">
                {societyName
                  ? t('drawers.editContact.unlinkSocietyWarningWithName', { ns: 'missionDashboard', name: societyName })
                  : t('drawers.editContact.unlinkSocietyWarning', { ns: 'missionDashboard' })}
              </InfoV2>
              <Box sx={{ fontSize: 11, fontWeight: 700, color: dsV2.color.t3, marginBottom: '6px' }}>
                {t('drawers.editContact.impactedMissions', { ns: 'missionDashboard' })}
              </Box>
              {loadingMissions ? (
                <Box sx={{ fontSize: 12, color: dsV2.color.t4, paddingBottom: '10px' }}>
                  {t('drawers.common.loading', { ns: 'missionDashboard' })}
                </Box>
              ) : impactedMissions.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '12px' }}>
                  {impactedMissions.map((mission) => (
                    <Box
                      key={mission.recordId}
                      sx={{
                        fontSize: '12px',
                        color: dsV2.color.noir,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          width: 5,
                          height: 5,
                          borderRadius: '50%',
                          background: mission.isActive ? dsV2.color.vert : dsV2.color.t4,
                        }}
                      />
                      {mission.label || mission.recordId}
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box sx={{ fontSize: 12, color: dsV2.color.t4, fontStyle: 'italic', marginBottom: '12px' }}>
                  {t('drawers.editContact.noOtherMission', { ns: 'missionDashboard' })}
                </Box>
              )}
            </>
          ) : (
            <InfoV2 tone="amber">{t('drawers.editContact.unlinkMissionWarning', { ns: 'missionDashboard' })}</InfoV2>
          )}
          <Box sx={{ display: 'flex', gap: '8px' }}>
            <ButtonV2 variant="secondary" onClick={() => setConfirming(false)} disabled={mutations.busy}>
              {t('drawers.common.cancel', { ns: 'missionDashboard' })}
            </ButtonV2>
            <ButtonV2
              variant="danger"
              onClick={confirmUnlink}
              loading={mutations.busy}
              startIcon={<LinkOffRoundedIcon sx={{ fontSize: 15 }} />}
            >
              {t('drawers.editContact.confirmUnlink', { ns: 'missionDashboard' })}
            </ButtonV2>
          </Box>
        </Box>
      )}
    </DrawerV2>
  );
};

export default ContactEditDrawer;
